'use client'

import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { RouteSpinner } from '@/components/ui/route-spinner'
import { useSessionMeta, updateAppSessionState } from '@/state/session/context'

interface OnboardingGuardProps {
  children: ReactNode
}

export function OnboardingGuard({ children }: OnboardingGuardProps): React.JSX.Element {
  const router = useRouter()
  const {
    hydrated,
    isLoading,
    isAuthenticated,
    firstAccess,
    onboardingCompleted,
    postOnboardingRedirect,
  } = useSessionMeta()
  const shouldExitOnboarding = onboardingCompleted && !firstAccess

  useEffect(() => {
    if (!hydrated || isLoading) return

    if (!isAuthenticated) {
      router.replace('/login')
      return
    }

    if (shouldExitOnboarding) {
      const target = postOnboardingRedirect ?? '/dashboard'
      if (postOnboardingRedirect) {
        updateAppSessionState((prev) => ({
          ...prev,
          postOnboardingRedirect: null,
        }))
      }
      router.replace(target)
    }
  }, [
    hydrated,
    router,
    firstAccess,
    isAuthenticated,
    isLoading,
    onboardingCompleted,
    postOnboardingRedirect,
    shouldExitOnboarding,
  ])

  if (!hydrated || isLoading || !isAuthenticated || shouldExitOnboarding) {
    return (
      <div className="mx-auto w-full max-w-md px-4 py-12">
        <RouteSpinner label="INICIANDO PROTOCOLO..." showLabel />
      </div>
    )
  }

  return <>{children}</>
}
